import { Card, CardContent } from '@/components/ui/card';
import { FileText, ArrowRight, LogOut, BookOpen, Banknote, Gavel } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function DocCard({ title, description, icon, path, tagColor, tag }) {
  const navigate = useNavigate();
  return (
    <Card
      className="group cursor-pointer hover:shadow-md transition-all duration-200 hover:border-orange-300"
      onClick={() => navigate(path)}
      data-testid={`doc-card-${path.replace(/\//g, '-')}`}
    >
      <CardContent className="py-5 px-6">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 shrink-0">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-base group-hover:text-orange-600 transition-colors">{title}</h3>
              {tag && (
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${tagColor}`}>{tag}</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{description}</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function DocsHubPage() {
  const documents = [
    {
      title: 'Employee Exit & Settlement',
      description: 'Step-by-step guide for managing employee notice periods, pro-rated settlements, finalization, and re-hire flows.',
      icon: <LogOut className="h-5 w-5" />,
      path: '/docs/exit-settlement',
      tag: '8 Steps',
      tagColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    },
    {
      title: 'Retention Bonus Payout',
      description: 'How to generate, review, edit, finalize, and pay half-yearly retention bonuses with pro-rating and role-based access.',
      icon: <Banknote className="h-5 w-5" />,
      path: '/docs/retention-bonus',
      tag: '8 Steps',
      tagColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    },
    {
      title: 'Penalty Management',
      description: 'Capture disciplinary shortcomings via Zoho Forms webhooks, map fields, review and acknowledge penalties, and deduct them through salary recovery.',
      icon: <Gavel className="h-5 w-5" />,
      path: '/docs/penalties',
      tag: '8 Steps',
      tagColor: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="docs-hub-page">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <BookOpen className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold tracking-tight">Documentation</h1>
        </div>
        <p className="text-muted-foreground mt-1 ml-9">Process guides and reference documents for PayTrack</p>
      </div>

      <div className="space-y-3">
        {(() => {
          const cards = [];
          for (let i = 0; i < documents.length; i++) {
            const d = documents[i];
            cards.push(
              <DocCard
                key={d.path}
                title={d.title}
                description={d.description}
                icon={d.icon}
                path={d.path}
                tag={d.tag}
                tagColor={d.tagColor}
              />
            );
          }
          return cards;
        })()}
      </div>

      <Card className="border-dashed bg-muted/20">
        <CardContent className="py-8 flex flex-col items-center justify-center text-center">
          <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">More documents will be added here as processes are documented.</p>
        </CardContent>
      </Card>
    </div>
  );
}
